import { Box, Button, Grid, Typography } from '@mui/material'
import { BerthResponseDto, BerthSide, PierResponseDto } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { BerthPlanningViewStore } from '@planning/pages/BerthPlanning/Stores/BerthPlanningViewStore'
import { VesselVisitBerthAndTimingFormStore } from '@planning/pages/VesselVisit/Stores/VesselVisitBerthAndTimingFormStore'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { RangeDropdown } from '../../atoms/RangeDropdown'

const dockingSides = [BerthSide.Port, BerthSide.Starboard]

export const BerthSelector = observer(
  ({
    store,
    berthPlanningViewStore,
  }: {
    store: VesselVisitBerthAndTimingFormStore
    berthPlanningViewStore: BerthPlanningViewStore
  }) => {
    const { t } = useTranslate()
    const { vesselVisitPlanningStore } = usePlanningStore()

    useEffect(() => {
      if (!store.dockingSide.value) {
        store.dockingSide.setValue(BerthSide.Port)
      }
    }, [store])

    useEffect(() => {
      // [Review] TODO: Extract and UT
      const vesselBerths = vesselVisitPlanningStore.vesselVisit?.berths
      const currentPierId = store.pier.value?.id

      const allBerths = Object.values(berthPlanningViewStore.berths).flat()

      const selectedBerthId = vesselBerths?.[0]?.id
      const selectedBerth = allBerths.find(b => b.id === selectedBerthId)

      const correctPierId = selectedBerth?.pierId

      if (
        berthPlanningViewStore.piers.length > 0 &&
        correctPierId &&
        currentPierId !== correctPierId
      ) {
        const correctPier = berthPlanningViewStore.piers.find(p => p.id === correctPierId)

        if (correctPier) {
          store.pier.setValue(correctPier)
          berthPlanningViewStore.setSelectPierId(correctPier.id)
        }
      }
    }, [store, berthPlanningViewStore, vesselVisitPlanningStore.vesselVisit])

    const handleDockingSideClick = (side: BerthSide) => {
      store.dockingSide.setValue(side)
    }

    const handlePierClick = (clickedPier: PierResponseDto) => {
      store.pier.setValue(clickedPier)
      berthPlanningViewStore.setSelectPierId(clickedPier.id)

      store.qmmFrom.setValue('')
      store.qmmTo.setValue('')
      store.berths.setValue([])
      berthPlanningViewStore.setSelectedQmmFrom(0)
      berthPlanningViewStore.setSelectedQmmTo(0)
      berthPlanningViewStore.setSelectedBerths([])
    }

    const piers = berthPlanningViewStore?.piersWithBerths

    return (
      <>
        <Grid size={{ xs: 12 }}>
          <Typography variant='subtitle1'>{t('berth', 'Berth')}</Typography>
        </Grid>

        {/* Docking Side */}
        <Grid size={{ xs: 12 }}>
          <Typography variant='subtitle2'>{t('dockingSide', 'Docking side')}</Typography>
          <Box display='flex' gap={1} mt={1}>
            {dockingSides.map(side => (
              <Button
                key={side}
                size='small'
                variant='contained'
                data-cy={`docking-side-button-${side.toLowerCase()}`}
                color={store.dockingSide.value === side ? 'secondary' : 'inherit'}
                onClick={() => handleDockingSideClick(side)}
              >
                {t(side.toLowerCase(), side)}
              </Button>
            ))}
          </Box>
        </Grid>

        {/* Pier */}
        <Grid size={{ xs: 12 }}>
          <Typography variant='subtitle2'>{t('pier', 'Pier')}</Typography>
          <Box display='flex' gap={1} mt={1} flexWrap='wrap'>
            {piers?.map((p, index) => {
              const isSelected = store.pier.value?.id === p.id

              return (
                <Button
                  key={p.id}
                  size='small'
                  variant='contained'
                  color={isSelected ? 'secondary' : 'inherit'}
                  onClick={() => handlePierClick(p)}
                  data-cy={`pier-button-${index}`}
                >
                  {p.name}
                </Button>
              )
            })}
          </Box>
        </Grid>

        {/* Berth */}
        <Grid size={{ xs: 12 }}>
          <Typography variant='subtitle2'>{t('berth', 'Berth')}</Typography>
          <Box display='flex' gap={1} mt={1} flexWrap='wrap'>
            <RangeDropdown
              required
              label={t('berth', 'Berth')}
              dataCy='berth-autocomplete'
              entityKey='id'
              options={store.berthsOfSelectedPier}
              getOptionLabel={(option: BerthResponseDto) => option.name}
              getRangeDisplayValue={(values: BerthResponseDto[]) =>
                values.map(v => v.name).join(', ')
              }
              sortFn={val => val.sort((a, b) => a.quayMeterMarkFrom - b.quayMeterMarkFrom)}
              handleChange={berthPlanningViewStore.setBerthAndPierAndQmm}
              value={store.berths.value}
              error={store.berths.isTouched && !!store.berths.error}
              helperText={store.berths.error ? t('fieldIsRequired') : ''}
              validate={store.validate}
            />
          </Box>
        </Grid>
      </>
    )
  },
)
